import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../shared/components/PageHeader';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { ProjectNote } from '../../core/types/data-models';

/**
 * Demonstrates the Scoped pattern in the UI: list/create/delete notes
 * attached to one Project at `/projects/:id/notes`. The dedicated service
 * is built from the parent id so the URL is correct.
 */
export function ProjectDetail() {
  const params = useParams<{ id: string }>();
  const projectId = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const projectQuery = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => resources.projects.get(projectId),
    enabled: Number.isFinite(projectId),
  });

  const summaryQuery = useQuery({
    queryKey: ['projects', projectId, 'validation-summary'],
    queryFn: () => resources.validationSummaryFor(projectId),
    enabled:
      Number.isFinite(projectId) &&
      (projectQuery.data?.projectType === 'IDEA_VALIDATION' ||
        projectQuery.data?.projectType === 'BUSINESS_VALIDATION' ||
        projectQuery.data?.projectType === 'STOCK_VALIDATION'),
  });
  const summary = summaryQuery.data ?? null;

  const notesService = resources.projectNotesFor(projectId);

  const notesQuery = useQuery({
    queryKey: ['projects', projectId, 'notes'],
    queryFn: () => notesService.list({ pageSize: 100, sort: '-id' }),
    enabled: Number.isFinite(projectId),
  });

  const invalidateNotes = () =>
    queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'notes'] });

  const addNote = useMutation({
    mutationFn: (text: string) => notesService.create({ body: text } as Partial<ProjectNote>),
    onSuccess: () => {
      invalidateNotes();
      setBody('');
    },
  });

  const removeNote = useMutation({
    mutationFn: (id: number) => notesService.remove(id),
    onSuccess: invalidateNotes,
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!body.trim()) return;
    try {
      await addNote.mutateAsync(body.trim());
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={projectQuery.data?.name ?? `Project #${projectId}`}
        description={projectQuery.data?.description ?? undefined}
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')}>
            Back
          </Button>
        }
      />

      {summary && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Validation summary
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              <strong>Status:</strong> {summary.status}
            </Typography>
            <Typography variant="body2">
              <strong>Recommendation:</strong> {summary.recommendation ?? '—'}
              {summary.overallScore != null ? ` (${summary.overallScore}/10)` : ''}
            </Typography>
            <Typography variant="body2">
              <strong>Decision:</strong> {summary.decision ?? '—'}
              {summary.decidedAt ? ` on ${new Date(summary.decidedAt).toLocaleDateString()}` : ''}
            </Typography>
            <Typography variant="body2">
              <strong>Generated:</strong> {new Date(summary.updatedAt).toLocaleString()}
              {summary.model ? ` · ${summary.model}` : ''}
              {summary.jobId != null ? ` · job #${summary.jobId}` : ''}
            </Typography>
          </Stack>
          {summary.decisionNote && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {summary.decisionNote}
            </Typography>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Add a note
        </Typography>
        <form onSubmit={handleAdd}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={1}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="A note about this project…"
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={addNote.isPending || !body.trim()}
            >
              Add
            </Button>
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </form>
      </Paper>

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1">
            Notes ({notesQuery.data?.page.total ?? 0})
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {(notesQuery.data?.data ?? []).map((note) => (
            <Stack
              key={note.id}
              direction="row"
              alignItems="center"
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                {note.body}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeNote.mutate(note.id)}
                aria-label="delete note"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {(notesQuery.data?.data.length ?? 0) === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No notes yet.
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
